import { useCallback, useEffect, useState } from "react";
import { pushEvent } from "./gtm";
import Step1Area from "./steps/Step1Area";
import Step2Phone from "./steps/Step2Phone";
import Step3Result from "./steps/Step3Result";
import type { QuoteData, QuoteResult, Step } from "./types";

interface Props {
  partner: string;
}

const TOTAL_STEPS = 3;

// Once per page load, regardless of React remounts (StrictMode double-effects).
let viewTracked = false;

export default function App({ partner }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [quoteData, setQuoteData] = useState<QuoteData | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [result, setResult] = useState<QuoteResult | null>(null);

  // Marketing funnel: calculator rendered.
  useEffect(() => {
    if (viewTracked) return;
    viewTracked = true;
    pushEvent("valuation_view", { partner });
  }, [partner]);

  const handleStep1 = useCallback((data: QuoteData, newQuoteId: string) => {
    setQuoteData(data);
    setQuoteId(newQuoteId);
    setStep(2);
    pushEvent("valuation_calculated", { partner });
  }, [partner]);

  const handleStep2 = useCallback((r: QuoteResult) => {
    setResult(r);
    setStep(3);
    pushEvent("generate_lead", { partner });
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: "wyceniarka_lead_captured", partner },
        "*"
      );
    }
  }, [partner]);

  return (
    <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-6 md:p-8">
      {/* Progress bar only from step 2 — showing "1 of 3" up front made the
          form read as a long questionnaire and hurt conversion. */}
      {step > 1 && (
        <div className="mb-6">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand transition-all"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>
      )}

      {step === 1 && <Step1Area partner={partner} onDone={handleStep1} />}
      {step === 2 && quoteData && quoteId && (
        <Step2Phone
          partner={partner}
          quoteId={quoteId}
          quoteData={quoteData}
          onDone={handleStep2}
        />
      )}
      {step === 3 && result && quoteId && (
        <Step3Result partner={partner} quoteId={quoteId} result={result} />
      )}
    </div>
  );
}
